// Age is stored backend-side as a loosely-typed string that already
// anticipates unit suffixes (see PatientUpdateRequest.age docstring:
// "supports text like '6m', '18d'") -- this module is the single place
// that (de)serializes that convention so every age input in the app stays
// in sync instead of re-implementing its own parsing.
//
// Convention:
//   Years  -> bare digits, unsuffixed, e.g. "2", "34"   (unchanged from before)
//   Months -> digits + "m", e.g. "6m", "23m"             (1-23; 24+ is a year)
//   Days   -> digits + "d", e.g. "0d", "18d"             (0-31; newborn precision)

import { getTodayIST } from './dateIST';

export const AGE_UNITS = [
  { value: 'years', label: 'Yrs', suffix: '', maxDigits: 3 },
  { value: 'months', label: 'Mo', suffix: 'm', maxDigits: 2 },
  { value: 'days', label: 'Days', suffix: 'd', maxDigits: 2 },
];

const UNIT_BY_VALUE = Object.fromEntries(AGE_UNITS.map((u) => [u.value, u]));
const MAX_BY_UNIT = { years: 999, months: 23, days: 31 };

/**
 * Split a stored age string into { amount, unit } for editing.
 * Unsuffixed digits -> years (today's format, unchanged). "<n>m" -> months.
 * "<n>d" -> days. Anything else (empty, or a legacy/malformed value that
 * doesn't match any of the above) is NOT coerced -- it's returned as-is
 * under "years" so it stays visible and round-trips unchanged through
 * serializeAgeValue if the user never touches the field.
 */
export function parseAgeValue(raw) {
  const value = (raw == null ? '' : String(raw)).trim();
  if (!value) return { amount: '', unit: 'years' };

  const monthsMatch = /^(\d{1,2})m$/i.exec(value);
  if (monthsMatch) return { amount: monthsMatch[1], unit: 'months' };

  const daysMatch = /^(\d{1,2})d$/i.exec(value);
  if (daysMatch) return { amount: daysMatch[1], unit: 'days' };

  if (/^\d{1,3}$/.test(value)) return { amount: value, unit: 'years' };

  return { amount: value, unit: 'years' };
}

/** Compose { amount, unit } back into the stored string convention. */
export function serializeAgeValue(amount, unit) {
  const trimmed = (amount == null ? '' : String(amount)).trim();
  if (!trimmed) return '';
  const suffix = UNIT_BY_VALUE[unit]?.suffix ?? '';
  return `${trimmed}${suffix}`;
}

/** Digits-only, length- and range-clamped to what `unit` allows. */
export function clampAgeAmount(raw, unit) {
  const meta = UNIT_BY_VALUE[unit] || UNIT_BY_VALUE.years;
  const digits = (raw || '').replace(/\D/g, '').slice(0, meta.maxDigits);
  if (digits === '') return digits;
  const max = MAX_BY_UNIT[unit] ?? Infinity;
  const num = parseInt(digits, 10);
  return num > max ? String(max) : digits;
}

// ── Age from DOB (OR-127: DOB known -> age derived, read-only) ──────────
// Mirror of the backend `patient_age_display` (app/utils/patient_display.py):
// SAME rules, kept in step by the shared vectors in __tests__/ageValue.test.js.

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'YYYY-MM-DD' -> { y, m, d } for a real calendar date, else null. */
function parseYmd(value) {
  const match = ISO_DATE_RE.exec(String(value ?? ''));
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(y, m - 1, d));
  const real = check.getUTCFullYear() === y && check.getUTCMonth() === m - 1 && check.getUTCDate() === d;
  return real ? { y, m, d } : null;
}

const utcDay = ({ y, m, d }) => Date.UTC(y, m - 1, d);

/**
 * Age derived from a DOB as of "today in IST" (never toISOString().slice(0,10)
 * -- that is the UTC date and is a day behind from IST midnight to 05:30).
 *   < 1 month -> days, < 1 year -> months, else completed years.
 * Returns { amount: string, unit: 'years'|'months'|'days' }, or null when the
 * DOB is missing, not a real YYYY-MM-DD date, or in the future.
 * `today` (a Date instant) pins the reference moment; default = now.
 */
export function ageFromDob(dob, today = new Date()) {
  const born = parseYmd(dob);
  const ref = parseYmd(getTodayIST(today));
  if (!born || !ref || utcDay(born) > utcDay(ref)) return null;

  const months = (ref.y - born.y) * 12 + (ref.m - born.m) - (ref.d < born.d ? 1 : 0);
  if (months < 1) {
    return { amount: String(Math.round((utcDay(ref) - utcDay(born)) / 86400000)), unit: 'days' };
  }
  if (months < 12) return { amount: String(months), unit: 'months' };
  return { amount: String(Math.floor(months / 12)), unit: 'years' };
}

const plural = (n, unit) => (n === 1 ? `${n} ${unit}` : `${n} ${unit}s`);

/**
 * "What age do we show for this patient" -- mirror of the backend
 * `patient_age_display`. A parseable, non-future `dob` WINS (days < 1 month,
 * months < 1 year, else bare years: "7 days", "8 months", "26"); otherwise the
 * stored `age` (compact infant forms "6m"/"18d" spelled out, any other string
 * verbatim, trimmed); otherwise "".
 */
export function patientAgeDisplay(patient, today) {
  const derived = ageFromDob(patient?.dob, today);
  if (derived) {
    const n = Number(derived.amount);
    if (derived.unit === 'days') return plural(n, 'day');
    if (derived.unit === 'months') return plural(n, 'month');
    return derived.amount;
  }
  const stored = String(patient?.age || '').trim();
  const infant = /^(\d{1,2})([md])$/i.exec(stored);
  if (infant) return plural(Number(infant[1]), infant[2].toLowerCase() === 'm' ? 'month' : 'day');
  return stored;
}
